import React, {Fragment, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useSearchParams} from 'react-router-dom';
import {
    Badge,
    Button,
    ButtonGroup, Label,
    ListGroup,
    ListGroupItem,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';
import { DatePicker } from '../../components/DateTime';
import FiltersLabel from '../../components/FiltersLabel';
import OrderCard from '../../components/OrderCard';
import Loading from '../../components/Loading';
import {nestedSearch} from '../../utils/nestedSearch';
import {fetchOrders, fetchPastOrders} from '../../slices/orders.slice';
import {fetchOrganizations} from '../../slices/organizations.slice';
import PageHeader from '../../components/PageHeader';
import { isAdmin } from '../../utils/admin';
import {fetchMovies} from '../../slices/movies.slice';
import OrderStatusBadge, {statusMap} from '../../components/OrderStatusBadge';
import SearchBar from '../../components/SearchBar';
import {imgUrl} from '../../utils/api-request';
import api from '../../api';
import ExportModal from '../../components/ExportModal';
import {getFiterDatesLabel} from '../../utils/dates';

const searchFields = [
    'status',
    { path: 'movie', fields: ['title'] },
    { path: 'version', fields: ['name'] },
];

// Current Orders and Past Orders each page independently, so each section
// carries its own pager rather than sharing one at the bottom of the view.
const Pager = ({ pagination, onChange }) => {
    if (pagination.totalPages <= 1) return null;

    return (
        <div className="d-flex justify-content-center align-items-center gap-3 my-4">
            <Button
                color="primary"
                outline
                size="sm"
                disabled={pagination.currentPage === 1}
                onClick={() => onChange(pagination.currentPage - 1)}
            >
                <span className="fas fa-chevron-left me-2"/>
                Previous
            </Button>

            <span className="text-white">
                Page {pagination.currentPage} of {pagination.totalPages}
            </span>

            <Button
                color="primary"
                outline
                size="sm"
                disabled={!pagination.hasMore}
                onClick={() => onChange(pagination.currentPage + 1)}
            >
                Next
                <span className="fas fa-chevron-right ms-2"/>
            </Button>
        </div>
    );
};

const Orders = () => {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        list: orders,
        status,
        pagination,
        pastList,
        pastStatus,
        pastPagination,
    } = useSelector(state => state.orders);
    const { user } = useSelector(state => state.auth);
    const { list: movies } = useSelector(state => state.movies);
    const { list: organizations } = useSelector(state => state.organizations);

    const parseURLFilters = () => {
        const statusParam = searchParams.get('status');
        const moviesParam = searchParams.get('movies');
        const organizationsParam = searchParams.get('organizations');
        const dateFromParam = searchParams.get('dateFrom');
        const dateToParam = searchParams.get('dateTo');
        const searchParam = searchParams.get('search');
        const pageParam = searchParams.get('page');
        const notOrderedYetParam = searchParams.get('notOrderedYet');
        const pastPageParam = searchParams.get('pastPage');

        return {
            status: statusParam ? statusParam.split(',') : [],
            movies: moviesParam ? moviesParam.split(',') : [],
            organizations: organizationsParam ? organizationsParam.split(',') : [],
            screeningDates: {
                from: dateFromParam ? new Date(dateFromParam) : null,
                to: dateToParam ? new Date(dateToParam) : null,
            },
            search: searchParam || '',
            page: pageParam ? parseInt(pageParam, 10) : 1,
            // Past Orders pages independently of Current Orders.
            pastPage: pastPageParam ? parseInt(pastPageParam, 10) : 1,
            notOrderedYet: notOrderedYetParam === 'true',
        };
    };

    const [filterBy, setFilterBy] = useState(parseURLFilters());
    const [searchValue, setSearchInput] = useState('');
    const [searchMoviesValue, setSearchMoviesValue] = useState('');
    const [exportModal, setExportModal] = useState(false);
    const [filterModal, setFilterModal] = useState({ isOpen: false, filter: '' });

    const toggleFilterModal = filter => {
        setFilterModal({ isOpen: !filterModal.isOpen, filter });
    };

    const formatDateForURL = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const updateURLParams = (newFilters) => {
        const params = new URLSearchParams();

        if (newFilters.status.length > 0) {
            params.set('status', newFilters.status.join(','));
        }
        if (newFilters.movies.length > 0) {
            params.set('movies', newFilters.movies.join(','));
        }
        if (newFilters.organizations.length > 0) {
            params.set('organizations', newFilters.organizations.join(','));
        }
        if (newFilters.screeningDates.from) {
            params.set('dateFrom', formatDateForURL(newFilters.screeningDates.from));
        }
        if (newFilters.screeningDates.to) {
            params.set('dateTo', formatDateForURL(newFilters.screeningDates.to));
        }
        if (newFilters.search) {
            params.set('search', newFilters.search);
        }
        if (newFilters.page && newFilters.page !== 1) {
            params.set('page', newFilters.page);
        }
        if (newFilters.pastPage && newFilters.pastPage !== 1) {
            params.set('pastPage', newFilters.pastPage);
        }
        if (newFilters.notOrderedYet) {
            params.set('notOrderedYet', 'true');
        }

        setSearchParams(params);
    };

    const toggleFilter = (filterKey, value) => {
        setFilterBy((prevFilterBy) => {
            const isSelected = prevFilterBy[filterKey].includes(value);
            const newFilters = {
                ...prevFilterBy,
                [filterKey]: isSelected
                    ? prevFilterBy[filterKey].filter((item) => item !== value)
                    : [...prevFilterBy[filterKey], value],
                page: 1,
            };
            updateURLParams(newFilters);
            return newFilters;
        });
    };

    const handleScreeningDate = (period) => (date) => {
        setFilterBy((prevFilterBy) => {
            const newFilters = {
                ...prevFilterBy,
                screeningDates: {
                    ...prevFilterBy.screeningDates,
                    [period]: date,
                },
                page: 1,
            };
            updateURLParams(newFilters);
            return newFilters;
        });
    };

    const handleSearch = e => {
        e.preventDefault();
        const { value } = e.target;
        const query = value.toLowerCase();
        setSearchInput(query);
        setSearchMoviesValue(value.toLowerCase());
    };

    const handleExport = async (fields, filetype = 'csv') => {
        try {
            const response = await api.get('/orders', {
                params: { ...filterBy, export: filetype, fields },
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `orders.${filetype}`);
            document.body.appendChild(link);
            link.click();

            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    // The slice sets 'loading', not 'pending' — checking for 'pending' meant the
    // spinner never appeared while the list was blanked, so a slow page change
    // flashed the empty state instead.
    const isLoading = ['idle', 'loading'].includes(status);
    const isLoadingPast = ['idle', 'loading'].includes(pastStatus);

    // Current and Past are two independent server queries rather than one page
    // split client-side: the list is sorted screening-date-descending, so
    // bucketing a single 50-row page put every past order out of reach.
    const currentOrders = useMemo(() => (
        searchMoviesValue ? nestedSearch([...orders], searchFields, searchMoviesValue) : orders
    ), [orders, searchMoviesValue]);

    const pastOrders = useMemo(() => (
        searchMoviesValue ? nestedSearch([...pastList], searchFields, searchMoviesValue) : pastList
    ), [pastList, searchMoviesValue]);

    const totalResults = filterBy.notOrderedYet
        ? pagination.totalCount
        : pagination.totalCount + pastPagination.totalCount;
    // Past Orders counts purchases, not documents — a title booked six times is
    // six rows — so "orders" would understate what the list actually shows.
    const resultsLabel = filterBy.notOrderedYet
        ? `title${totalResults === 1 ? '' : 's'}`
        : `order${totalResults === 1 ? '' : 's'} & purchase${totalResults === 1 ? '' : 's'}`;
    const isEmpty = currentOrders.length === 0 && (filterBy.notOrderedYet || pastOrders.length === 0);

    const areFiltersActive = useMemo(() => {
        const { status, movies, screeningDates, organizations } = filterBy;

        return (
            status.length > 0 ||
            movies.length > 0 ||
            organizations.length > 0 ||
            screeningDates.from !== null ||
            screeningDates.to !== null
        );
    }, [filterBy]);

    const toggleNotOrderedYet = () => {
        setFilterBy((prevFilterBy) => {
            const newFilters = {
                ...prevFilterBy,
                notOrderedYet: !prevFilterBy.notOrderedYet,
                page: 1,
                pastPage: 1,
            };
            updateURLParams(newFilters);
            return newFilters;
        });
    };

    const clearFilters = () => {
        const newFilters = {
            status: [],
            movies: [],
            screeningDates: { from: null, to: null },
            organizations: [],
            search: '',
            page: 1,
            pastPage: 1,
            notOrderedYet: false,
        };
        setFilterBy(newFilters);
        updateURLParams(newFilters);
        setSearchInput('');
        setSearchMoviesValue('');
    };

    const handlePageChange = (key) => (newPage) => {
        const newFilters = {
            ...filterBy,
            [key]: newPage,
        };
        setFilterBy(newFilters);
        updateURLParams(newFilters);
    };

    // Everything the two queries share. Held apart from `page`/`pastPage` so
    // paging one section does not refetch — and blank — the other; the setters
    // all spread the previous state, so these keep their identity across a page
    // change and the effects below stay put.
    const { notOrderedYet, page, pastPage } = filterBy;
    const baseParams = useMemo(() => ({
        status: filterBy.status,
        movies: filterBy.movies,
        organizations: filterBy.organizations,
        screeningDates: filterBy.screeningDates,
        search: filterBy.search,
        notOrderedYet: filterBy.notOrderedYet,
    }), [filterBy.status, filterBy.movies, filterBy.organizations, filterBy.screeningDates, filterBy.search, filterBy.notOrderedYet]);

    useEffect(() => {
        // "Not ordered yet" is a filter over the whole catalogue, so it replaces
        // the current/past split rather than sitting alongside it.
        dispatch(fetchOrders(notOrderedYet
            ? { ...baseParams, page }
            : { ...baseParams, page, timeframe: 'current' }));
    }, [dispatch, baseParams, page, notOrderedYet]);

    useEffect(() => {
        if (notOrderedYet) return;

        dispatch(fetchPastOrders({ ...baseParams, page: pastPage }));
    }, [dispatch, baseParams, pastPage, notOrderedYet]);

    useEffect(() => {
        if (isAdmin(user)) {
            dispatch(fetchOrganizations());
        }
        dispatch(fetchMovies());
    }, [dispatch, user]);

    return (
        <div className="page px-4 py-3 d-flex flex-column overflow-y-auto">
            <PageHeader
                title='orders'
                searchBar={{searchValue, handleSearch}}
            />

            <div className="contentContainer">
                <div className="d-flex justify-content-start align-items-center gap-3">
                    <FiltersLabel active={areFiltersActive} onClear={clearFilters}/>

                    <ButtonGroup className="gap-2">
                        <Button color="primary" outline size="sm" className="rounded-3" onClick={() => toggleFilterModal('status')}>
                            {filterBy.status.length > 0 ? (
                                <Badge color="success" className="me-2 text-white">
                                    {filterBy.status.length}
                                </Badge>
                            ) : (
                                <span className="fas fa-sort me-2" />
                            )}
                            Status
                        </Button>

                        <Button color="primary" outline size="sm" className="rounded-3" onClick={() => toggleFilterModal('movies')}>
                            {filterBy.movies.length > 0 ? (
                                <Badge color="success" className="me-2 text-white">
                                    {filterBy.movies.length}
                                </Badge>
                            ) : (
                                <span className="fas fa-film me-2"/>
                            )}
                            Movies
                        </Button>

                        {isAdmin(user) && (
                            <Button color="primary" outline size="sm" className="rounded-3" onClick={() => toggleFilterModal('organizations')}>
                                {filterBy.organizations.length > 0 ? (
                                    <Badge color="success" className="me-2 text-white">
                                        {filterBy.organizations.length}
                                    </Badge>
                                ) : (
                                    <span className="fas fa-building me-2"/>
                                )}
                                Organizations
                            </Button>
                        )}

                        <Button color="primary" outline size="sm" className="rounded-3" onClick={() => toggleFilterModal('screening')}>
                            {filterBy.screeningDates.from || filterBy.screeningDates.to ? (
                                <Badge color="success" className="me-2 text-white">
                                    <span className="fas fa-calendar-check"/>
                                </Badge>
                            ) : (
                                <span className="fas fa-calendar-week me-2"/>
                            )}
                            {getFiterDatesLabel(filterBy.screeningDates, 'Screening Dates')}
                        </Button>

                        {isAdmin(user) && (
                            <Button
                                color="primary"
                                outline={!filterBy.notOrderedYet}
                                size="sm"
                                className="rounded-3"
                                onClick={toggleNotOrderedYet}
                            >
                                {filterBy.notOrderedYet ? (
                                    <span className="fas fa-toggle-on me-2 text-success"/>
                                ) : (
                                    <span className="fas fa-toggle-off me-2"/>
                                )}
                                Not Ordered Yet
                            </Button>
                        )}
                    </ButtonGroup>

                    <div className="flex-grow-1">
                        <span className="fas fa-list me-2 text-primary" />
                        showing {totalResults} {resultsLabel}
                    </div>

                    <div className="text-primary">
                        {isAdmin(user) && (
                            <Button color="link" onClick={() => setExportModal(!exportModal)} className="text-decoration-none">
                                <span className="fas fa-file-excel me-1" /> Export
                            </Button>
                        )}
                    </div>
                </div>

                {isLoading && isLoadingPast && <Loading />}

                {!isLoading && !isLoadingPast && isEmpty && (
                    <div className="d-flex flex-column align-items-center py-5">
                        <span className="fas fa-inbox fa-3x text-white-50 mb-3 mt-5 pt-5" />
                        {areFiltersActive || searchMoviesValue ? (
                            <>
                                <p className="text-white-50 mb-3">No orders match your filters</p>
                                <Button color="primary" outline onClick={clearFilters}>
                                    Clear filters
                                </Button>
                            </>
                        ) : (
                            <p className="text-white-50">No orders yet</p>
                        )}
                    </div>
                )}

                {filterBy.notOrderedYet ? (
                    !isLoading && currentOrders.length > 0 && (
                        <Fragment>
                            <h2 className="mt-5 text-uppercase">Not Ordered Yet</h2>
                            {currentOrders.map(order => (
                                <OrderCard key={order._id} order={order} />
                            ))}
                            <Pager pagination={pagination} onChange={handlePageChange('page')} />
                        </Fragment>
                    )
                ) : (
                    <Fragment>
                        {!isLoading && currentOrders.length > 0 && (
                            <Fragment>
                                <h2 className="mt-5 text-uppercase">Current Orders</h2>
                                {currentOrders.map(order => (
                                    <OrderCard key={order._id} order={order} />
                                ))}
                                <Pager pagination={pagination} onChange={handlePageChange('page')} />
                            </Fragment>
                        )}

                        {!isLoadingPast && pastOrders.length > 0 && (
                            <Fragment>
                                <h2 className="mt-5 text-uppercase">Past Orders</h2>
                                {/* One row per purchase, not per order: the same
                                    document is bought over and over, so a row per
                                    document showed only the latest booking of a
                                    title and hid every earlier one. */}
                                {pastOrders.map(order => (
                                    <OrderCard
                                        key={`${order._id}-${order.purchase?.index ?? 0}`}
                                        order={order}
                                        purchase={order.purchase}
                                    />
                                ))}
                                <Pager pagination={pastPagination} onChange={handlePageChange('pastPage')} />
                            </Fragment>
                        )}
                    </Fragment>
                )}

                <div className="clearfix py-4" />

                <span className="fas "></span>
            </div>

            <Modal isOpen={filterModal.isOpen} toggle={() => toggleFilterModal()} size="lg" scrollable>
                <ModalHeader toggle={() => toggleFilterModal()}>
                    Filter by {filterModal.filter}
                </ModalHeader>
                <ModalBody>

                    {filterModal.filter === 'status' && (
                        <div className="status-filter-modal">
                            {Object.keys(statusMap).map(status => (
                                <div
                                    key={status}
                                    className="my-1 position-relative pointer"
                                    onClick={() => toggleFilter('status', status)}
                                >
                                    {filterBy.status.includes(status) && (
                                        <span className="end-0 fa-check fas me-3 mt-2 position-absolute pt-1 top-0"/>
                                    )}

                                    <OrderStatusBadge
                                        status={status}
                                        className={`p-2 ${filterBy.status.includes(status) ? 'selected' : ''}`}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {filterModal.filter === 'movies' && (
                        <div className="movies-filter-modal">
                            <SearchBar onChange={({ target }) => setSearchMoviesValue(target.value)} initialValue={searchMoviesValue} />
                            <ListGroup>
                                {movies.map(({ _id, title, poster, assetDetails }) => title.toLowerCase().includes(searchMoviesValue.toLowerCase()) && (
                                    <ListGroupItem
                                        onClick={() => toggleFilter('movies', _id)}
                                        key={_id}
                                        style={{ cursor: 'pointer', borderRadius: '0' }}
                                        active={filterBy.movies.includes(_id)}
                                        className="border-0 border-start border-primary border-2 position-relative
                                            text-white d-flex justify-content-start gap-3 align-items-center mb-2"
                                    >
                                        <img src={imgUrl(poster?.imageUrl)} width={50} alt=""/>
                                        <div className="flex-grow-1">
                                            {title}
                                        </div>
                                        {assetDetails && (
                                            <Badge color="primary" className="text-black text-uppercase">{assetDetails.contentKind}</Badge>
                                        )}
                                        {filterBy.movies.includes(_id) && (
                                            <div className="position-absolute top-0 end-0 mt-2 me-3 small">
                                                Selected <span className="fas fa-check-circle text-success"/>
                                            </div>
                                        )}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </div>
                    )}

                    {filterModal.filter === 'screening' && (
                        <div className="screening-filter-modal">
                            <Label>Screening date from</Label>
                            <DatePicker
                                value={filterBy.screeningDates.from}
                                onChange={handleScreeningDate('from')}
                                placeholder="From"
                                clearable
                            />
                            <Label className="mt-4">Screening date to</Label>
                            <DatePicker
                                value={filterBy.screeningDates.to}
                                onChange={handleScreeningDate('to')}
                                placeholder="To"
                                clearable
                            />
                        </div>
                    )}

                    {filterModal.filter === 'organizations' && (
                        <div className="organizations-filter-modal">
                            <SearchBar onChange={({ target }) => setSearchMoviesValue(target.value)} initialValue={searchMoviesValue} />
                            <ListGroup>
                                {organizations.map(({ _id, name, logo }) => name.toLowerCase().includes(searchMoviesValue.toLowerCase()) && (
                                    <ListGroupItem
                                        onClick={() => toggleFilter('organizations', _id)}
                                        key={_id}
                                        style={{ cursor: 'pointer', borderRadius: '0' }}
                                        active={filterBy.organizations.includes(_id)}
                                        className="border-0 border-start border-primary border-2 position-relative
                                            text-white d-flex justify-content-start gap-3 align-items-center mb-2"
                                    >
                                        <img src={imgUrl(logo?.imageUrl)} width={50} alt=""/>
                                        <div className="flex-grow-1">
                                            {name}
                                        </div>
                                        {filterBy.organizations.includes(_id) && (
                                            <div className="position-absolute top-0 end-0 mt-2 me-3 small">
                                                Selected <span className="fas fa-check-circle text-success"/>
                                            </div>
                                        )}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => toggleFilterModal()}>
                        <span className="fas fa-check-circle me-2" />
                        Show results
                    </Button>
                </ModalFooter>
            </Modal>

            <ExportModal
                isOpen={exportModal}
                toggle={() => setExportModal(!exportModal)}
                onExport={handleExport}
                entityName="Orders"
                fields={[
                    'status',
                    'statusWorkflow',
                    'requestStatus',
                    'orderType',
                    'isTrailer',
                    'price',
                    'screeningDate',
                ]}
                subFields={{
                    organization: ['name', 'creditAmount', 'country', 'isYacht', 'gofilexCompanyId', 'lastSynced'],
                    movie: ['title', 'releaseYear', 'status', 'genres', 'youtubeTrailerId'],
                    version: ['name', 'price', 'releaseDate', 'defaultHirePeriod', 'hirePeriodType', 'isMovieOnRequest', 'tier', 'uuid'],
                }}
                defaultSelectedFields={[
                    'movie.title',
                    'version.name',
                    'organization.name',
                    'organization.country',
                    'status',
                    'statusWorkflow',
                    'requestStatus',
                    'orderType',
                    'price',
                    'screeningDate',
                ]}
            />
        </div>
    );
};

export default Orders;
